import useTranslations from "@/shared/lang/useTranslations";

import { DEFAULT_POCKET_COLOR, resolvePocketName } from "../constants/pockets.constants";
import type { Pocket } from "../types/pockets-api.types";
import { PocketIcon } from "./PocketIcon";

type PocketPreviewPillProps = {
  pocket: Pocket;
  onOpen: (pocketId: string) => void;
};

const formatUSD = (raw: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw);

export const PocketPreviewPill = ({ pocket, onOpen }: PocketPreviewPillProps) => {
  const { t } = useTranslations("cards");
  const name = resolvePocketName(pocket);
  const color = pocket.metadata?.color || DEFAULT_POCKET_COLOR;
  const cardsCount = pocket.cards_count ?? 0;
  const balance =
    typeof pocket.balance === "number" ? pocket.balance : parseFloat(pocket.balance as string) || 0;

  return (
    <button
      type="button"
      onClick={() => onOpen(pocket.id)}
      className="flex w-[220px] shrink-0 items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-all hover:border-ring/40 hover:shadow-[var(--shadow-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <PocketIcon icon={pocket.metadata?.icon} color={color} size={36} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{name}</div>
        <div className="text-[11px] text-muted-foreground">
          {cardsCount} {t("identities_label_cards").toLowerCase()} ·{" "}
          <span className="font-mono">{formatUSD(balance)}</span>
        </div>
      </div>
    </button>
  );
};
